
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { type Crop as CropType } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ProfilePhotoDisplay } from "./photo-upload/ProfilePhotoDisplay";
import { PhotoCropDialog } from "./photo-upload/PhotoCropDialog";
import { centerAspectCrop, getCroppedImg } from "./photo-upload/utils";
import { uploadPhoto } from "./photo-upload/uploadPhoto";

interface PhotoUploadProps {
  imageUrl: string | null;
  onPhotoUpdate: (url: string) => void;
}

export const PhotoUpload = ({ imageUrl, onPhotoUpdate }: PhotoUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<CropType>();
  const [userId, setUserId] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        setUserId(session.user.id);
      }
    };
    
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height, 1);
    setCrop(crop);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "Please sign in to upload a photo",
        variant: "destructive",
      });
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(file);
      setPreviewUrl(reader.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "Please sign in to upload a photo",
        variant: "destructive",
      });
      return;
    }

    if (!imgRef.current || !crop) {
      toast({
        title: "Error",
        description: "Please select an area to crop",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      
      const img = imgRef.current;
      const croppedBlob = await getCroppedImg(img, crop);
      const croppedFile = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });

      const publicUrl = await uploadPhoto(croppedFile, userId, imageUrl);
      
      onPhotoUpdate(publicUrl);
      setShowCropDialog(false);
      setPreviewUrl(null);
      setSelectedFile(null);

      toast({
        title: "Success",
        description: "Profile photo updated successfully",
      });
    } catch (error: any) {
      console.error('Photo upload error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>Profile Picture</Label>
      <div className="mt-2 space-y-4">
        <ProfilePhotoDisplay imageUrl={imageUrl} />
        <Input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
          id="photo-upload"
        />
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => document.getElementById('photo-upload')?.click()}
          disabled={uploading}
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? "Uploading..." : imageUrl ? "Change Photo" : "Upload Photo"}
        </Button>
      </div>

      <PhotoCropDialog
        open={showCropDialog}
        onOpenChange={setShowCropDialog}
        previewUrl={previewUrl}
        crop={crop}
        onCropChange={setCrop}
        onSave={handlePhotoUpload}
        onCancel={() => {
          setShowCropDialog(false);
          setPreviewUrl(null);
          setSelectedFile(null);
        }}
        uploading={uploading}
        imgRef={imgRef}
        onImageLoad={onImageLoad}
      />
    </div>
  );
};
